import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/auth";
import { HFButton } from "@/components/ui-hf";
import { Save, Check } from "lucide-react";
import { IMPRESSUM, DATENSCHUTZ, IMPRESSUM_DEFAULT, DATENSCHUTZ_DEFAULT, BDV_KONTAKT_DEFAULT } from "@/lib/legalContent";

function fallbackToMarkdown(page: typeof IMPRESSUM): string {
    const lines: string[] = [`# ${page.title}`, "", `_${page.intro}_`, ""];
    page.sections.forEach((s) => {
        lines.push(`## ${s.heading}`, "");
        s.body.forEach((b) => lines.push(b, ""));
    });
    return lines.join("\n");
}

const TABS = [
    { key: "legal.impressum", label: "Impressum", fallback: IMPRESSUM_DEFAULT || fallbackToMarkdown(IMPRESSUM) },
    { key: "legal.datenschutz", label: "Datenschutz", fallback: DATENSCHUTZ_DEFAULT || fallbackToMarkdown(DATENSCHUTZ) },
    { key: "legal.bdv_kontakt", label: "Kontakt (BDV)", fallback: BDV_KONTAKT_DEFAULT },
];

export default function AdminRechtstextePage() {
    const [active, setActive] = useState(TABS[0].key);
    const [values, setValues] = useState<Record<string, string>>({});
    const [tick, setTick] = useState<string | null>(null);
    const [error, setError] = useState("");

    useEffect(() => {
        void (async () => {
            const res = await api<{ items: Record<string, string> }>("/api/admin/site-content");
            if (res.ok && res.data) {
                setValues({
                    "legal.impressum": res.data.items["legal.impressum"] || TABS[0].fallback,
                    "legal.datenschutz": res.data.items["legal.datenschutz"] || TABS[1].fallback,
                    "legal.bdv_kontakt": res.data.items["legal.bdv_kontakt"] || TABS[2].fallback,
                });
            } else {
                setError(res.error || "Konnte Inhalte nicht laden.");
            }
        })();
    }, []);

    const save = async (key: string) => {
        const res = await api(`/api/admin/site-content/${encodeURIComponent(key)}`, {
            method: "PUT", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value: values[key] || "" }),
        });
        if (res.ok) {
            setTick(key);
            window.setTimeout(() => setTick(null), 1500);
        } else {
            setError(res.error || "Speichern fehlgeschlagen.");
        }
    };

    const md = values[active] || "";

    return (
        <div data-testid="admin-rechtstexte">
            <div className="hf-eyebrow">Rechtstexte</div>
            <h1 className="font-display text-3xl md:text-4xl mt-2">Impressum, Datenschutz & Kontakt (BDV)</h1>
            <p className="mt-1 text-hf-muted">Markdown mit Live-Vorschau. Änderungen ersetzen die Standard-Texte.</p>

            <div className="mt-6 flex items-center gap-2">
                {TABS.map((t) => (
                    <button key={t.key} onClick={() => setActive(t.key)}
                        className="px-4 py-2 rounded-sm text-sm"
                        style={{
                            background: active === t.key ? "hsl(var(--hf-ink))" : "hsl(var(--hf-bg))",
                            color: active === t.key ? "hsl(var(--hf-bg))" : "hsl(var(--hf-ink))",
                            border: "1px solid hsl(var(--hf-line))",
                        }}
                        data-testid={`rechtstext-tab-${t.key}`}>
                        {t.label}
                    </button>
                ))}
                <div className="flex-1" />
                <HFButton onClick={() => void save(active)} variant="primary" icon={tick === active ? <Check size={13} /> : <Save size={13} />} data-testid="rechtstext-save">
                    {tick === active ? "Gespeichert" : "Speichern"}
                </HFButton>
            </div>

            {error && <div className="mt-4 p-3 text-sm rounded" style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}>{error}</div>}

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Editor · Markdown</div>
                    <textarea value={md}
                        onChange={(e) => setValues({ ...values, [active]: e.target.value })}
                        rows={26}
                        className="mt-2 w-full px-4 py-3 border rounded-sm bg-white font-mono text-sm"
                        style={{ borderColor: "hsl(var(--hf-line))" }}
                        data-testid="rechtstext-editor" />
                </div>
                <div>
                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Live-Vorschau</div>
                    <div className="mt-2 p-6 rounded-sm prose max-w-none overflow-auto"
                        style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))", maxHeight: 700 }}
                        data-testid="rechtstext-preview">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
                    </div>
                </div>
            </div>
        </div>
    );
}
